/** Lógica de entrar y crear cuenta. Compartida por las dos variantes. */

import { useState } from 'react';

import { ErrorAPI, guardarToken, pedir } from '../datos/api.ts';
import { MINIMO_CLAVE } from './useClave.ts';

export type Modo = 'entrar' | 'crear';

export function useEntrar(onEntro: () => void) {
  const [modo, setModo] = useState<Modo>('entrar');
  const [nombre, setNombre] = useState('');
  const [email, setEmail] = useState('');
  const [clave, setClave] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sinCuenta, setSinCuenta] = useState(false);

  /** El formulario arranca en "entrar" y nada dice cuál de las dos cosas está haciendo. */
  const titulo = modo === 'crear' ? 'Crear cuenta' : 'Entrar';

  const cambiarModo = (siguiente: Modo): void => {
    setModo(siguiente);
    setError(null);
    setSinCuenta(false);
  };

  const enviar = async (): Promise<void> => {
    setEnviando(true);
    setError(null);
    setSinCuenta(false);
    try {
      const ruta = modo === 'crear' ? '/auth/registro' : '/auth/login';
      const cuerpo =
        modo === 'crear'
          ? {
              nombre,
              email,
              clave,
              // La zona del dispositivo: define cuándo cambia el día para el calendario y para
              // "hoy". Un servidor en UTC haría cambiar de día a las 21:00 en Chile.
              zona_horaria: Intl.DateTimeFormat().resolvedOptions().timeZone,
            }
          : { email, clave };

      const r = await pedir<{ token: string }>(ruta, { metodo: 'POST', cuerpo });
      guardarToken(r.token);
      onEntro();
    } catch (e) {
      setError(e instanceof ErrorAPI ? e.message : 'No se pudo entrar.');
      // "Email o clave incorrectos" es también lo que ve alguien que todavía no tiene cuenta: el
      // servidor no dice si el email existe —y está bien que no lo diga—, así que el formulario no
      // puede distinguirlos. Ofrece la salida en vez de dejarlo probando claves que no existen.
      setSinCuenta(e instanceof ErrorAPI && modo === 'entrar' && e.codigo === 'credenciales');
    } finally {
      setEnviando(false);
    }
  };

  /**
   * El largo mínimo se exige al CREAR la clave, no al entrar.
   *
   * Validar el largo en el login es un error de principio: quien ya tiene una clave corta —porque
   * se sembró, porque el mínimo cambió después— quedaría encerrado afuera de su propia cuenta por
   * una regla del formulario. Al entrar, el único que decide si la clave sirve es el servidor.
   */
  const valido =
    email.includes('@') &&
    (modo === 'crear'
      ? clave.length >= MINIMO_CLAVE && nombre.trim().length > 0
      : clave.length > 0);

  /**
   * Qué falta para poder enviar, en una frase.
   *
   * El formulario arranca vacío, así que el botón arranca deshabilitado: es lo primero que ve
   * alguien que abre la app por primera vez, y un botón gris sin explicación es una pared
   * (§3 del contrato de feedback). Nombra los campos que faltan de verdad, no una regla genérica,
   * y sirve para los dos modos: al entrar no se exige largo de clave, sólo que haya una.
   */
  const queFalta = ((): string | null => {
    if (valido) return null;

    const faltan: string[] = [];
    if (modo === 'crear' && nombre.trim().length === 0) faltan.push('nombre');
    if (!email.includes('@')) faltan.push('email');
    if (clave.length < (modo === 'crear' ? MINIMO_CLAVE : 1)) faltan.push('clave');
    if (faltan.length === 0) return null;

    // Un email escrito pero sin arroba no es un campo vacío: decir "completá email" mandaría a
    // escribir de nuevo algo que ya está escrito, sin decir qué tiene de malo.
    if (faltan.length === 1 && faltan[0] === 'email' && email.trim().length > 0) {
      return 'Al email le falta el @.';
    }

    const ultimo = faltan[faltan.length - 1] ?? '';
    const lista = faltan.length === 1 ? ultimo : `${faltan.slice(0, -1).join(', ')} y ${ultimo}`;
    // El mínimo se nombra sólo cuando la clave es parte de lo que falta, y sólo al crearla.
    const minimo =
      modo === 'crear' && faltan.includes('clave') ? ` (mínimo ${MINIMO_CLAVE} caracteres)` : '';
    return `Completá ${lista}${minimo}.`;
  })();

  return {
    modo,
    titulo,
    cambiarModo,
    nombre,
    setNombre,
    email,
    setEmail,
    clave,
    setClave,
    enviando,
    error,
    /** El error fue de credenciales entrando: puede ser que la cuenta ni siquiera exista. */
    sinCuenta,
    enviar,
    valido,
    /** Por qué el botón está deshabilitado. `null` cuando el formulario ya se puede enviar. */
    queFalta,
  };
}
